import { defineStore } from 'pinia'

import { relaunchApi } from '@/logic/relaunchApi'
import store from '@/store'

export function getCacheKey(morphType, foreignId, userIds) {
  const sortedUserIds = Array.isArray(userIds) ? [...userIds].sort() : []
  return `${morphType}-${foreignId}-${sortedUserIds.join(',')}`
}

export const useContentAccessStore = defineStore('contentAccessStore', {
  state: () => ({
    contentAccessData: {},
    pendingRequests: {},
  }),

  getters: {
    getCachedContentAccess: (state) => (morphType, foreignId, userIds = []) => {
      const cacheKey = getCacheKey(morphType, foreignId, userIds)
      return state.contentAccessData[cacheKey] || null
    },
  },

  actions: {
    async fetchContentAccessData(morphType, foreignId, userIds) {
      const cacheKey = getCacheKey(morphType, foreignId, userIds)

      const cached = this.getCachedContentAccess(morphType, foreignId, userIds)

      if (cached) {
        return cached
      }

      if (this.pendingRequests[cacheKey]) {
        return this.pendingRequests[cacheKey]
      }

      this.pendingRequests[cacheKey] = (async () => {
        try {
          const result = await this._performApiRequest(morphType, foreignId, userIds)
          // Cache the result for identical subsequent requests,
          // and to prevent multiple API calls when switching threads,
          // or even re-rendering the chat messages
          this.contentAccessData[cacheKey] = {
            entity: result.entity,
            usersAccess: result.usersAccess,
          }
          return result
        } finally {
          delete this.pendingRequests[cacheKey]
        }
      })()

      return this.pendingRequests[cacheKey]
    },

    async _performApiRequest(morphType, foreignId, userIds) {
      try {
        const body = {
          morph_type: morphType,
          foreign_id: foreignId,
          user_ids: userIds,
        }

        const response = await relaunchApi.post('/global/content/check-access', body)
        const data = response.data || response

        return {
          entity: data.entity,
          usersAccess: data.users_access,
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch content access:', error)
        throw error
      }
    },

    async getContentAccessData(morphType, foreignId, userIds) {
      const data = await this.fetchContentAccessData(morphType, foreignId, userIds)
      const currentUserId = store.getters['auth/userId']

      const currentUserAccess = data.usersAccess.find((user) => user.user_id === currentUserId)
      const currentUserHasAccess = currentUserAccess?.has_access || false

      // Get users without access
      const deniedUsers = data.usersAccess
        .filter((user) => !user.has_access)
        .map((user) => ({
          id: user.user_id,
          username: user.username,
        }))

      return {
        entity: data.entity,
        currentUserHasAccess,
        deniedUsers,
      }
    },
  },
})
